import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Building2, User, Phone, Mail, MapPin, Hash, Loader2, Wallet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtGram, fmtDate } from "@/lib/format";

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 py-3 border-b border-border">
      <Icon size={18} strokeWidth={1.5} className="text-gold shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] text-muted-foreground">{label}</div>
        <div className="text-sm text-foreground truncate">{value || "—"}</div>
      </div>
    </div>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/balance").then((r) => setBalance(r.data)).catch(() => setBalance(null)).finally(() => setLoading(false));
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const bal = balance?.balance_has || 0;

  return (
    <div data-testid="account-page">
      <h1 className="font-display text-3xl text-foreground mb-6">Hesabım</h1>

      <div className="bg-surface border border-border rounded-md p-5 mb-4" data-testid="balance-card">
        <div className="flex items-center gap-2 text-muted-foreground mb-1">
          <Wallet size={15} strokeWidth={1.5} />
          <span className="text-[10px] tracking-luxe uppercase">Has Altın Bakiyesi</span>
        </div>
        {loading ? (
          <Loader2 className="animate-spin text-gold mt-2" size={22} />
        ) : (
          <>
            <div data-testid="balance-amount" className={`text-3xl font-semibold mt-1 ${bal > 0 ? "text-gold" : "text-emerald-400"}`}>
              {fmtGram(bal)} gr
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              {bal > 0 ? "ORSE GOLD'a olan has altın borcunuz" : bal < 0 ? "Lehinize bakiye (alacak)" : "Bakiyeniz sıfır"}
            </div>
          </>
        )}
      </div>

      {balance?.entries?.length > 0 && (
        <div className="bg-surface border border-border rounded-md p-4 mb-6">
          <div className="text-[10px] tracking-luxe uppercase text-gold mb-3">Hesap Ekstresi</div>
          <div className="space-y-2">
            {balance.entries.map((e) => (
              <div key={e.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div className="min-w-0">
                  <div className="text-sm text-foreground">{e.kind}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{e.description} · {fmtDate(e.created_at)}</div>
                </div>
                <div className={`text-sm font-medium ${e.delta_has >= 0 ? "text-gold" : "text-emerald-400"}`}>
                  {e.delta_has >= 0 ? "+" : ""}{fmtGram(e.delta_has)} gr
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-surface border border-border rounded-md p-5 mb-6">
        <div className="text-[10px] tracking-luxe uppercase text-gold mb-1">Firma</div>
        <div className="font-display text-2xl text-foreground">{user?.company_name || user?.name}</div>
        <div className="mt-4">
          <Row icon={User} label="Yetkili Kişi" value={user?.contact_person} />
          <Row icon={Phone} label="Telefon" value={user?.phone} />
          <Row icon={Mail} label="E-posta" value={user?.email} />
          <Row icon={Hash} label="Müşteri Kodu" value={user?.customer_code} />
          <Row icon={MapPin} label="Adres" value={user?.address} />
          <Row icon={Building2} label="Hesap Durumu" value={user?.active ? "Aktif" : "Pasif"} />
        </div>
      </div>

      <button
        data-testid="customer-logout-button"
        onClick={doLogout}
        className="w-full h-12 border border-border rounded-md text-red-400 hover:bg-red-500/10 hover:border-red-500/40 transition-colors flex items-center justify-center gap-2"
      >
        <LogOut size={18} /> Çıkış Yap
      </button>
    </div>
  );
}
